/* eslint-disable no-unused-vars */
import { useEffect, useState } from "react";
import {
  Container,
  Grid,
  Card,
  CardContent,
  Typography,
  Button,
  TextField,
  Loading,
  Box,
} from "../../components";
import { CadastrosService, notification } from "../../services";
import { sxInputLogin } from "../login/constantes.login";
import { formatCnpj, formatCpf, responseErros } from "../../utils";

export const emojis = [
  {
    emoji: "ph-fill ph-smiley-angry",
    label: "Muito insatisfeito",
    color: "#eb5a5a",
    variant: "error",
  },
  {
    emoji: "ph-fill ph-smiley-sad",
    label: "Insatisfeito",
    color: "#f48a00",
    variant: "warning",
  },
  {
    emoji: "ph-fill ph-smiley-meh",
    label: "Neutro",
    color: "#fbbc05",
    variant: "warning",
  },
  {
    emoji: "ph-fill ph-smiley",
    label: "Satisfeito",
    color: "#5D8E3C",
    variant: "success",
  },
  {
    emoji: "ph-fill ph-smiley-wink",
    label: "Muito satisfeito",
    color: "#164221",
    variant: "success",
  },
];

const sizes = {
  width: {
    xs: 60,
    md: 85,
  },
  height: {
    xs: 60,
    md: 85,
  },
  fontSize: {
    xs: 30,
    sm: 35,
  },
};

function PesquisaSatisfacaoView({ match }) {
  const { id } = match.params;
  const [loading, setLoading] = useState(false);
  const [pesquisa, setPesquisa] = useState({});
  const [erroPesquisaMensagem, setErroPesquisaMensagem] = useState(null);
  const cadastrosService = new CadastrosService();
  const footerHeight = 64;

  useEffect(() => {
    buscarPesquisaSatisfacao();
  }, [id]);

  const buscarPesquisaSatisfacao = async () => {
    setLoading(true);
    const hash = location.pathname.replace(
      "/cadastros/pesquisa-satisfacao/",
      "",
    );
    const res = await cadastrosService.getPesquisaSatisfacao(hash);
    setLoading(false);
    if (!res?.isAxiosError) {
      setPesquisa(res.data || {});
      setErroPesquisaMensagem(null);
    } else {
      const apiMsg = res?.response?.data?.message || res?.message;
      setErroPesquisaMensagem(
        Array.isArray(apiMsg) ? apiMsg.join(", ") : apiMsg,
      );
      responseErros(res);
    }
  };

  const handleRate = (perguntaId, value) => {
    setPesquisa((prev) => ({
      ...prev,
      perguntas: (prev.perguntas || []).map((q) =>
        q.id === perguntaId ? { ...q, resposta: value } : q,
      ),
    }));
  };

  const handleSubmit = async () => {
    const obrigatorias = (pesquisa.perguntas || []).filter(
      (p) => p.obrigatoriedade,
    );
    const faltando = obrigatorias.find((p) => !p.resposta);
    if (faltando) {
      notification.alertaGenericos(
        "Por favor responda as perguntas obrigatórias.",
      );
      return;
    }

    const body = (pesquisa.perguntas || []).map((p) => ({
      ...p,
      id: p.id,
      pergunta: p.pergunta,
      resposta: p.resposta ?? null,
      type: p.type || null,
    }));

    const res = await cadastrosService.updatePesquisaSatisfacao(body);
    setLoading(false);
    if (!res?.isAxiosError) {
      notification.sucessoGenericos("Pesquisa enviada com sucesso!");
      setErroPesquisaMensagem(
        "Pesquisa de satisfação já respondida para esta ordem de serviço",
      );
    } else {
      responseErros(res);
    }
  };

  const empresaLabel = (() => {
    const cnpjCpf = pesquisa?.cnpjCpf;
    if (!pesquisa?.nomeFantasia || !cnpjCpf) return "";
    const cnpjFormatado =
      cnpjCpf?.length > 11 ? formatCnpj(cnpjCpf) : formatCpf(cnpjCpf);
    return `${pesquisa.nomeFantasia} - ${cnpjFormatado}`;
  })();

  return (
    <Box
      className="bg-white w-100"
      sx={{
        minHeight: "100dvh",
        boxSizing: "border-box",
        paddingBottom: `${footerHeight}px`,
      }}
    >
      <Grid container justifyContent="center" py={4} px={2}>
        <Grid item xs={12} sm={8} lg={5}>
          <Box className="mb-4">
            <Typography
              color="primary"
              variant="h1"
              className="text-uppercase fw-bolder"
              sx={{
                fontSize: {
                  xs: 25,
                  md: 40,
                },
                lineHeight: 1.2,
              }}
            >
              Avaliação <br />
              de Satisfação
            </Typography>
            <Typography variant="body2" color="textSecondary">
              {pesquisa.clienteNome ? `Cliente: ${pesquisa.clienteNome}` : ""}
            </Typography>
            <Typography variant="body2" color="textSecondary">
              {pesquisa.serieDocumento && pesquisa.numeroDocumento
                ? `Série: ${pesquisa.serieDocumento} | Nº ${pesquisa.numeroDocumento}`
                : ""}
            </Typography>
          </Box>

          {erroPesquisaMensagem ? (
            <Card className="mb-4 rounded-4">
              <CardContent>
                <Typography variant="h6" className="mb-2">
                  Pesquisa indisponível
                </Typography>
                <Typography
                  variant="body2"
                  color="textSecondary"
                  className="mb-3"
                >
                  {erroPesquisaMensagem}
                </Typography>
              </CardContent>
            </Card>
          ) : (
            (pesquisa?.perguntas || []).map((p) => (
              <Card key={p.id} className="mb-4 rounded-4">
                <CardContent>
                  <Typography variant="subtitle2" className="mb-4">
                    {p.pergunta}
                    <span className="text-danger">
                      {p.obrigatoriedade ? " *" : ""}
                    </span>
                  </Typography>

                  {p.type === "number" ? (
                    <div className="d-flex align-items-center gap-3 justify-content-center flex-wrap">
                      {[1, 2, 3, 4, 5].map((n) => {
                        const idx = n - 1;
                        const em = emojis[idx] || {};
                        return (
                          <Box
                            key={n}
                            className="d-flex flex-column align-items-center"
                          >
                            <Button
                              className="rounded-4"
                              disableElevation
                              variant={
                                p.resposta === n ? "contained" : "outlined"
                              }
                              color={em.variant || "primary"}
                              sx={{
                                width: {
                                  xs: sizes.width.xs,
                                  md: sizes.width.md,
                                },
                                height: {
                                  xs: sizes.height.xs,
                                  md: sizes.height.md,
                                },
                                minWidth: {
                                  xs: sizes.width.xs,
                                  md: sizes.width.md,
                                },
                                padding: 0,
                                backgroundColor:
                                  p.resposta === n ? em.color : undefined,
                                borderColor: em.color,
                                "&:hover": {
                                  backgroundColor:
                                    p.resposta !== n
                                      ? em.color + "20"
                                      : em.color,
                                },
                                "& i": {
                                  fontSize: {
                                    xs: sizes.fontSize.xs,
                                    sm: sizes.fontSize.sm,
                                  },
                                  lineHeight: 1,
                                },
                              }}
                              onClick={() => handleRate(p.id, n)}
                            >
                              {em.emoji && (
                                <i
                                  className={em.emoji}
                                  style={{
                                    color: p.resposta === n ? "#fff" : em.color,
                                  }}
                                ></i>
                              )}
                            </Button>
                            {em.label && (
                              <Typography
                                variant="body2"
                                gutterBottom
                                className="mt-2 text-center lh-1"
                                sx={{
                                  width: {
                                    xs: sizes.width.xs,
                                    md: sizes.width.md,
                                  },
                                  height: 20,
                                  fontSize: 11,
                                }}
                              >
                                {em.label}
                              </Typography>
                            )}
                          </Box>
                        );
                      })}
                    </div>
                  ) : (
                    <>
                      <TextField
                        variant="standard"
                        type="text"
                        multiline
                        minRows={3}
                        fullWidth
                        InputProps={{
                          disableUnderline: true,
                        }}
                        sx={sxInputLogin}
                        size="small"
                        value={p.resposta || ""}
                        onChange={(e) => handleRate(p.id, e.target.value)}
                        inputProps={{ maxLength: 200 }}
                      />
                      {p.resposta && (
                        <Typography
                          variant="caption"
                          color="textSecondary"
                          className="d-block text-end mt-2"
                        >
                          {p.resposta.length}/200
                        </Typography>
                      )}
                    </>
                  )}
                </CardContent>
              </Card>
            ))
          )}
          <Button
            variant="contained"
            className="fw-bold rounded-3"
            disableElevation
            fullWidth
            color="primary"
            onClick={handleSubmit}
            disabled={!!erroPesquisaMensagem}
          >
            Enviar
          </Button>
        </Grid>
      </Grid>
      <Box
        sx={{
          position: "fixed",
          bottom: 0,
          left: 0,
          right: 0,
          height: footerHeight,
          backgroundColor: "#fff",
          borderTop: "1px solid rgba(0,0,0,0.08)",
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
          zIndex: 1200,
          px: 2,
        }}
      >
        <Typography variant="body2" color="textSecondary">
          {empresaLabel}
        </Typography>
      </Box>
      <Loading loading={loading} />
    </Box>
  );
}

export default PesquisaSatisfacaoView;
